import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DoCheck,
  HostBinding,
  HostListener,
  inject,
  input,
  Input,
  output,
  QueryList,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { IconComponent } from '@app-shared/components/icon/icon.component';
import { untilDestroyed } from '@app-shared/utils';
import { map, startWith, tap } from 'rxjs/operators';

import { DropdownOptionComponent } from './dropdown-option.component';

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, IconComponent],
})
export class DropdownComponent implements ControlValueAccessor, AfterContentInit, DoCheck {
  private cdr = inject(ChangeDetectorRef);
  control = inject(NgControl, { self: true, optional: true });

  @ContentChildren(DropdownOptionComponent) optionComponents: QueryList<DropdownOptionComponent>;

  readonly placeholder = input<string>('');
  @Input() label: string = '';
  readonly required = input<boolean>(false);

  @Input() @HostBinding('class.ui-read-only') readOnly: boolean = false;
  @Input() @HostBinding('class.ui-inline-view') inlineView: boolean = false;
  @Input() labelFormatter: (value: string) => string;

  readonly change = output<string>();

  options: { value: string; label: string }[] = [];
  isExpanded: boolean = false;

  value: string = '';
  disabled: boolean = false;
  errorMessage: string = '';

  readonly #untilDestroyed = untilDestroyed();

  constructor() {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  @HostListener('focusout') onTouched(): void {
    this.propagateTouched();
  }

  get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, errors } = this.control;
    if (errors) {
      if (errors.errorMessage) {
        this.errorMessage = errors.errorMessage;
      }
    }

    return this.invalid;
  }

  get invalid(): boolean {
    return this.control ? this.control.invalid && (this.control.dirty || this.control.touched) : false;
  }

  get valueLabel(): string {
    const found = this.options.find(
      (o: { value: string; label: string }) => JSON.stringify(o.value) === JSON.stringify(this.value)
    );

    return found?.label || null;
  }

  get hasValue(): boolean {
    return !(this.value === undefined || this.value === null || this.value === '');
  }

  ngDoCheck(): void {
    // replace with IterableDiffers maybe
    const options = this.optionComponents?.map(({ value: valueSignal, label }) => {
      const valueInput = valueSignal();
      const value = valueInput;

      return { value, label };
    });
    if (JSON.stringify(this.options) !== JSON.stringify(options)) {
      if (options) {
        this.options = options;
      }
    }
  }

  ngAfterContentInit(): void {
    this.optionComponents.changes
      .pipe(
        startWith(this.optionComponents),
        map((optionComponents: QueryList<DropdownOptionComponent>) => optionComponents.toArray()),
        tap((optionComponents: DropdownOptionComponent[]) => {
          this.options = optionComponents.map(({ value: valueSignal, label: labelInput }) => {
            const label = labelInput;

            return { value: valueSignal(), label };
          });
          this.cdr.markForCheck();
        }),
        this.#untilDestroyed()
      )
      .subscribe();
  }

  select(value: string): void {
    if (this.disabled) {
      return;
    }

    if (this.value !== value) {
      const found = this.options.find((o) => o.value === value);

      if (found) {
        this.value = found.value;
        this.cdr.markForCheck();
      }

      this.propagateChange(this.value);
      this.change.emit(this.value);
    }

    this.closeDropdown();
  }

  toggleExpand(): void {
    if (this.disabled || this.readOnly || !this.options.length) {
      return;
    }

    this.isExpanded = !this.isExpanded;
  }

  closeDropdown(): void {
    this.isExpanded = false;
  }

  writeValue(value: string): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  propagateChange = (_: string) => undefined;
  propagateTouched = () => undefined;

  registerOnChange(fn): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => unknown): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }
}
